import axios from 'axios';
import { expandSelectChoices, normalizeSchemaAttributes } from '@/utils/selectChoices';
import { bumpDomainListRevision, getDomainListRevision } from '@/utils/domainState';

const apiClient = axios.create({
	baseURL: 'https://be-chub-full-integration-bfbjh8ewhfgpg4g6.westeurope-01.azurewebsites.net',
    //baseURL: 'http://localhost:5000',
	headers: {
		'Content-Type': 'application/json',
	},
});

apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');

    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }

    // N4 output localization: pass the selected UI locale so the backend
    // generates LLM natural-language content in that language. Mirrors the
    // app locale (already normalized to 'en' / 'ja' in plugins/i18n.js);
    // anything that isn't Japanese falls back to English on the backend.
    try {
        const savedLanguage = localStorage.getItem('appLanguage');
        config.headers['Accept-Language'] =
            String(savedLanguage || '').toLowerCase().startsWith('ja') ? 'ja' : 'en';
    } catch (e) {
        config.headers['Accept-Language'] = 'en';
    }

    return config;
});

apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;
        if (status === 401) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('currentUser');
            window.dispatchEvent(new CustomEvent('auth:expired'));
        } else if (status === 403) {
            // Valid token but insufficient role/group: surface a message, do NOT log out.
            window.dispatchEvent(new CustomEvent('auth:forbidden', {
                detail: error.response?.data?.detail,
            }));
        }
        return Promise.reject(error);
    }
);

// Method for the /ask_male_22 endpoint
export const askMale22 = (question) => {
	return apiClient.post('/ask_male_22', question, {
		headers: { 'Content-Type': 'text/plain' },
	});
};

// Method for the /ask_persona endpoint
export const ask_persona = (formData) => {
	return apiClient.post('/ask_persona', formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});
};

export const generate_interview_questions = (formData) => {
	return apiClient.post('/generate_questions', formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});
};

// Translates already-generated questions into target_language (e.g. 'en'/'ja')
// without a full regeneration -- used to switch the display language of an
// existing question set. questions: [{ question, file_id }].
export const translate_interview_questions = (questions, target_language) => {
	return apiClient.post('/translate_questions', { questions, target_language });
};

export const start_interview = (formData) => {
	return apiClient.post('/api/start_interviews', formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});
};

export const start_focus_group = (formData) => {
	return apiClient.post('/api/start_focus_group', formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});
};

// Template-based runs have no question list and never carry a file: the
// stimulus materials are uploaded beforehand (upload_file, one call per file)
// and referenced by `attachment_ids`, so this one is plain JSON (the client's
// default header). Request and response shapes: utils/templateAttachment.js.
export const start_template_focus_group = (payload) => {
	return apiClient.post('/api/start_template_focus_group', payload);
};

// Uploads one file for later use by id (today: in the attachment_ids of a
// template-based focus group). `domain` must be the domain of the template that
// will use it. The multipart header is required: with the client's JSON
// default, axios would serialize the FormData to JSON.
export const upload_file = (file, domain, { signal, onUploadProgress } = {}) => {
	const formData = new FormData();
	formData.append('file', file, file.name);
	formData.append('domain', domain);
	return apiClient.post('/api/v1/files', formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
		signal,
		onUploadProgress,
	});
};

// Same shape as the upload response, with page links signed now. They expire:
// fetch them each time the pages are shown, never store them.
export const get_file = (file_id) => {
	return apiClient.get(`/api/v1/files/${file_id}`);
};

// Method for the /generate_new_thread_id endpoint
export const generate_new_thread_id = (persona_id) => {
	return apiClient.post('/generate_new_thread_id', {persona_id}); // creates new entry in the MongoDB database
};

// deletes chat conversation
export const delete_thread_id = (thread_id, persona_id) => {
    return apiClient.delete(`/thread/${persona_id}/${thread_id}`);
};

export const fetch_thread_ids = (persona_id) => {
    return apiClient.get(`/thread_ids/${persona_id}`);
};

// Method to get conversation with a persona by thread ID
export const get_conversation_with_persona = (thread_id) => {
	return apiClient.get(`/conversation/${thread_id}`);
};

// Fetch campaigns from the backend
export const fetch_campaigns = (selected_domain) => {
	return apiClient.get(`/topics/${selected_domain}`);
};

export const fetch_persona_names = (selected_domain) => {
	return apiClient.get(`/personas/${selected_domain}/names`);
};

export const list_persona_fields = () => {
	return apiClient.get('/personas/list_fields');
};

export const GENERATED_PERSONA_TAGS = ['ai_generated'];

export const create_or_update_persona = (name, domain, data, persona_id = null, tags = null) => {
	const resolvedTags = tags ?? (persona_id ? [] : GENERATED_PERSONA_TAGS);
	return apiClient.post('/personas', { name, domain, data, persona_id, tags: resolvedTags });
};

export const generate_persona = (domain, data, save_after_generation = false) => {
	return apiClient.post('/personas/generate', { domain, data, save_after_generation });
};

export const delete_persona = (persona_id) => {
	return apiClient.delete(`/personas/${persona_id}`);
};

export const fetch_personas_by_domain = (selected_domain) => {
	return apiClient.get(`/personas/${selected_domain}`);
};

export const fetch_generated_personas = (selected_domain) => {
	return apiClient.get(`/personas/${selected_domain}/generated`);
};

// All personas persisted in Mongo for a domain (manual saves + AI-generated).
export const fetch_saved_personas_by_domain = async (selected_domain) => {
	const results = await Promise.allSettled([
		fetch_personas_by_domain(selected_domain),
		fetch_generated_personas(selected_domain),
	]);
	const byId = new Map();
	results.forEach((result) => {
		if (result.status !== 'fulfilled') return;
		(result.value.data || []).forEach((doc) => {
			if (doc?.persona_id) {
				byId.set(doc.persona_id, doc);
			}
		});
	});
	return { data: Array.from(byId.values()) };
};

export const get_personas_chart_data = (personas_list) => {
    return apiClient.post("/api/get_personas_chart_data", { personas_list });
};

export const get_campaign_timestamps = (campaign_id) => {
    return apiClient.get(`/api/get_campaign_timestamps/${campaign_id}`);
};

export const get_campaign_bar_chart_data = (campaign_id, timestamp) => {
    return apiClient.get(`/api/get_campaign_bar_chart_data/${campaign_id}/${timestamp}`);
};

export const fetch_focus_groups = (selected_domain) => {
    return apiClient.get(`/api/focus-groups/domain/${selected_domain}`);
};

export const get_focus_group = (focus_group_id) => {
    return apiClient.get(`/api/focus-groups/${focus_group_id}`);
};

export const delete_focus_group = (focus_group_id) => {
    return apiClient.delete(`/api/focus-groups/${focus_group_id}`);
};

export const fetch_interview_runs = (selected_domain) => {
    return apiClient.get(`/api/interview-runs/domain/${selected_domain}`);
};

export const get_interview_run = (run_id) => {
    return apiClient.get(`/api/interview-runs/${run_id}`);
};

export const delete_interview_run = (run_id) => {
    return apiClient.delete(`/api/interview-runs/${run_id}`);
};

// Runs are tracked per domain: pass the domain to get its run's status (without
// it the backend returns the latest run among all the caller's domains).
export const get_background_run_status = (domain) => {
    return apiClient.get('/api/background-run/status', { params: { domain } });
};

export const get_interview_run_chart_data = (run_id) => {
    return apiClient.get(`/api/interview-runs/${run_id}/chart-data`);
};

// Method to add a new topic
export const add_campaign = (campaign_name, description, domain) => {
	return apiClient.post('/topics', {
		topic_name: campaign_name,
		description,
		domain
	});
};

export const delete_campaign = (topic_id) => {
    return apiClient.delete(`/topics/${topic_id}`);
};

export const register_user = ({ email, username, password }) => {
    return apiClient.post('/api/v1/auth/register', {
        email,
        username,
        password,
    });
};

export const login_user = ({ email, password }) => {
    return apiClient.post('/api/v1/auth/login', {
        email,
        password,
    });
};

export const fetch_current_user = () => {
    return apiClient.get('/api/v1/auth/loggedUser');
};

export const logout_user = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
};

export const set_auth_session = (loginResponse) => {
    const { access_token, user } = loginResponse.data;

    localStorage.setItem('authToken', access_token);
    localStorage.setItem('currentUser', JSON.stringify(user));

    return user;
};

export const get_current_user_from_storage = () => {
    const rawUser = localStorage.getItem('currentUser');

    if (!rawUser) {
        return null;
    }

    try {
        return JSON.parse(rawUser);
    } catch {
        return null;
    }
};


export const fetch_domains = () => {
    return apiClient.get('/api/v1/domains');
};

export const create_domain = (name, displayName = null) => {
    return apiClient.post('/api/v1/domains', {
        name,
        display_name: displayName,
    });
};

export const update_domain = async (domainId, updates) => {
    const response = await apiClient.patch(`/api/v1/domains/${domainId}`, updates);
    await refresh_current_user();
    return response;
};

export const delete_domain = async (domainId) => {
    const response = await apiClient.delete(`/api/v1/domains/${domainId}`);
    await refresh_current_user();
    return response;
};

function normalizeAttributePayload(attribute) {
    if (!attribute?.options?.choices) return attribute;
    const choices = expandSelectChoices(attribute.options.choices);
    if (!choices.length) return attribute;
    return {
        ...attribute,
        options: {
            ...attribute.options,
            choices,
        },
    };
}

export const fetch_persona_domain_schema = async (domainId) => {
    const response = await apiClient.get(`/api/v1/domains/${domainId}/persona-schema`);
    if (response?.data?.attributes) {
        response.data.attributes = normalizeSchemaAttributes(response.data.attributes);
    }
    return response;
};

export const save_persona_domain_schema = (domainId, attributes) => {
    const normalized = normalizeSchemaAttributes(attributes);
    return apiClient.put(`/api/v1/domains/${domainId}/persona-schema`, { attributes: normalized });
};

export const add_persona_domain_attribute = (domainId, attribute) => {
    return apiClient.post(
        `/api/v1/domains/${domainId}/persona-schema/attributes`,
        normalizeAttributePayload(attribute),
    );
};

export const update_persona_domain_attribute = (domainId, attributeId, updates) => {
    return apiClient.patch(
        `/api/v1/domains/${domainId}/persona-schema/attributes/${attributeId}`,
        normalizeAttributePayload(updates),
    );
};

export const fetch_persona_attribute_delete_impact = (domainId, attributeId) => {
    return apiClient.get(
        `/api/v1/domains/${domainId}/persona-schema/attributes/${attributeId}/delete-impact`,
    );
};

export const delete_persona_domain_attribute = (domainId, attributeId) => {
    return apiClient.delete(`/api/v1/domains/${domainId}/persona-schema/attributes/${attributeId}`);
};

export const refresh_current_user = async () => {
    const response = await fetch_current_user();
    const user = response.data;
    const previousDomains = JSON.stringify(get_current_user_from_storage()?.allowed_domains ?? []);
    localStorage.setItem('currentUser', JSON.stringify(user));
    // The bump remounts the current page (App.vue keys the view on
    // domainRevision), so only do it when the domain list really changed —
    // not on every startup refresh, which made the page load twice.
    if (JSON.stringify(user?.allowed_domains ?? []) !== previousDomains) {
        bumpDomainListRevision();
    }
    return user;
};

// Known domain values → existing i18n label keys, so translated names are preserved.
// Any domain not in this map falls back to the backend-provided `name`.
const DOMAIN_I18N_KEYS = {
    telco: 'generic.telco',
    automotive: 'generic.automotive',
    top_management: 'generic.topManagement',
    energy: 'generic.energy',
};

// The domains the logged-in user may see, already resolved by the backend.
// Shape: [{ value, name }]. Empty for a non-admin whose group has no domains yet.
export const get_allowed_domains = () => {
    const user = get_current_user_from_storage();
    return Array.isArray(user?.allowed_domains) ? user.allowed_domains : [];
};

function resolve_domain_option_title(domain, t) {
    const slug = domain.value;
    const displayName = (domain.name || '').trim();
    // Admin-renamed domains carry a custom display name — prefer it over i18n.
    if (displayName && displayName !== slug) {
        return displayName;
    }
    const key = DOMAIN_I18N_KEYS[slug];
    if (key && typeof t === 'function') {
        return t(key);
    }
    return displayName || slug;
}

// Build the option list for a domain <v-select> from the user's allowed_domains.
// `t` is the component's this.$t so known domains keep their translated label.
export const build_domain_options = (t) => {
    void getDomainListRevision();
    return get_allowed_domains().map((domain) => ({
        title: resolve_domain_option_title(domain, t),
        value: domain.value,
    }));
};

/** Return domainId if still allowed, otherwise the new default (or null). */
export const coerce_allowed_domain = (domainId) => {
    const allowed = get_allowed_domains().map((d) => d.value);
    if (domainId && allowed.includes(domainId)) {
        return domainId;
    }
    return get_default_domain();
};

// Default domain to preselect: prefer 'telco' (the app's historical default) when the
// user is allowed to see it, otherwise the first allowed domain, or null when they have none.
export const get_default_domain = () => {
    const domains = get_allowed_domains();
    const telco = domains.find((d) => d.value === 'telco');
    return (telco ?? domains[0])?.value ?? null;
};


export const create_user = ({
    email,
    username,
    password,
    role_id = 'user',
    allowed_domains = [],
}) => {
    return apiClient.post('/api/v1/users', {
        email,
        username,
        password,
        role_id,
        allowed_domains,
    });
};

export const fetch_users = ({ skip = 0, limit = 50 } = {}) => {
    return apiClient.get('/api/v1/users', {
        params: {
            skip,
            limit,
        },
    });
};

export const fetch_user_by_id = (userId) => {
    return apiClient.get(`/api/v1/users/${userId}`);
};

export const update_user = (userId, data) => {
    return apiClient.patch(`/api/v1/users/${userId}`, data);
};

export const delete_user = (userId) => {
    return apiClient.delete(`/api/v1/users/${userId}`);
};

export const fetch_prompts = ({ domain, prompt_type, execution_mode, group_id, skip = 0, limit = 100 } = {}) => {
    const params = {};
    if (domain) params.domain = domain;
    if (prompt_type) params.prompt_type = prompt_type;
    if (execution_mode) params.execution_mode = execution_mode;
    if (group_id) params.group_id = group_id;
    params.skip = skip;
    params.limit = limit;
    return apiClient.get('/api/v1/prompts', { params });
};

export const get_prompt = (promptId) => {
    return apiClient.get(`/api/v1/prompts/${promptId}`);
};

export const create_prompt = (data) => {
    return apiClient.post('/api/v1/prompts', data);
};

export const update_prompt = (promptId, data) => {
    return apiClient.patch(`/api/v1/prompts/${promptId}`, data);
};

export const delete_prompt = (promptId) => {
    return apiClient.delete(`/api/v1/prompts/${promptId}`);
};

export const copy_prompt = (promptId) => {
    return apiClient.post(`/api/v1/prompts/${promptId}/copy`);
};

export const fetch_meeting_templates = ({ domain, group_id, skip = 0, limit = 100 } = {}) => {
    const params = {};
    if (domain) params.domain = domain;
    if (group_id) params.group_id = group_id;
    params.skip = skip;
    params.limit = limit;
    return apiClient.get('/api/v1/meeting-templates', { params });
};

export const get_meeting_template = (templateId) => {
    return apiClient.get(`/api/v1/meeting-templates/${templateId}`);
};

export const create_meeting_template = (data) => {
    return apiClient.post('/api/v1/meeting-templates', data);
};

export const update_meeting_template = (templateId, data) => {
    return apiClient.patch(`/api/v1/meeting-templates/${templateId}`, data);
};

export const delete_meeting_template = (templateId) => {
    return apiClient.delete(`/api/v1/meeting-templates/${templateId}`);
};


export default apiClient;
